import { LINHA_DO_DRE, linhasDoTipo, type Destino } from "@bookdental/schemas";
import { reais, type CategoriaFinanceira } from "@/lib/dinheiro";
import { cn } from "@/lib/cn";
import { campo } from "../estilos";
import { destinoEmTexto, textoEmDestino, type GrupoDoRotulo } from "./montar";

type Props = { grupos: GrupoDoRotulo[]; destinos: Record<string, Destino>; categorias: CategoriaFinanceira[]; onChange: (rotulo: string, d: Destino) => void };

const escolhiveis = (cs: CategoriaFinanceira[], tipo: "ENTRADA" | "SAIDA") => cs.filter((c) => c.tipo === tipo && c.ativa && c.linhaDoDre !== "PERDAS_COM_CLIENTES");

/** Passo 3: cada conta da planilha vai para uma categoria (existente ou nova) ou fica de fora. */
export function PassoCategorias({ grupos, destinos, categorias, onChange }: Props) {
  return (
    <div className="space-y-2">
      <p className="text-xs text-zinc-500 dark:text-muted-foreground">
        As sugestões vêm do nome. Totais e subtotais da planilha (Receita bruta, Lucro, Resultado) ficam de fora. A categoria decide se a linha é entrada ou saída.
      </p>
      <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-50 text-left text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:bg-muted/40 dark:text-muted-foreground">
              <th className="px-3 py-2">Na planilha</th>
              <th className="px-3 py-2 text-right">Linhas</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2">Vai para</th>
            </tr>
          </thead>
          <tbody>
            {grupos.map((g) => {
              const d = destinos[g.rotulo] ?? { acao: "ignorar" };
              return (
                <tr key={g.rotulo} className={cn("border-t border-zinc-100 dark:border-border/50", d.acao === "ignorar" && "text-zinc-400")}>
                  <td className="max-w-[240px] truncate px-3 py-1.5 font-medium">{g.rotulo}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{g.quantidade}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{reais(g.totalCents)}</td>
                  <td className="px-3 py-1.5">
                    <select aria-label={`Destino de ${g.rotulo}`} value={destinoEmTexto(d)} onChange={(e) => onChange(g.rotulo, textoEmDestino(e.target.value))} className={cn(campo, "h-8 min-w-[220px]")}>
                      <option value="ignorar">Ignorar (total ou subtotal)</option>
                      {(["SAIDA", "ENTRADA"] as const).map((tipo) => (
                        <optgroup key={tipo} label={tipo === "SAIDA" ? "Categorias de saída" : "Categorias de entrada"}>
                          {escolhiveis(categorias, tipo).map((c) => (
                            <option key={c.id} value={`cat:${c.id}`}>
                              {c.nome}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                      {(["SAIDA", "ENTRADA"] as const).map((tipo) => (
                        <optgroup key={`nova-${tipo}`} label={`Criar "${g.rotulo.slice(0, 30)}" como ${tipo === "SAIDA" ? "saída" : "entrada"} em`}>
                          {linhasDoTipo(tipo)
                            .filter((l) => l !== "PERDAS_COM_CLIENTES")
                            .map((l) => (
                              <option key={l} value={`nova:${tipo}:${l}`}>
                                {LINHA_DO_DRE[l].rotulo}
                              </option>
                            ))}
                        </optgroup>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
